import {
  FINISHED_TO_ORDER,
  ADDITIONAL_PROPERTIES,
  LOWER_LEG_SLIM_SHORT_SKU,
  LOWER_LEG_SLIM_LONG_SKU,
  LEG
} from '@consts/index'
import { isMatchingVariantForOptions } from './isMatchingVariantForOptions'
import { getSortedProductOptionTypes } from './getSortedProductOptionTypes'
import { getValueFromObject } from '@helpers/utils'
import {
  ProductOption,
  ProductVariant,
  ProductWithVariants
} from 'types/product'
import { EmptyObject } from 'types/helpers'
import { FilterOption } from 'types/filters'

export function checkIfLowerLegRulesApply(filterOptions: FilterOption[] = []) {
  return filterOptions?.some(
    filter => filter.type === 'leg' && Number(filter.value) >= 33
  )
}

export function getAllAvailableProductOptionValues(
  product: ProductWithVariants | EmptyObject = {},
  filterOptions: FilterOption[] = [],
  nameOfFilter = ''
) {
  const { variants, options } = product || {}
  const productOptionTypes = getSortedProductOptionTypes(options)

  const productOptionsList = variants
    ? variants.reduce(
      (
        gatherVariant: Omit<ProductOption, 'name' | 'position'>[],
        variant
      ) => {
        const newGatherVariant = [...gatherVariant]

        let keepVariant = true

        variant.options
          ?.map(option => option.value)
          .forEach((optionValue, i) => {
            const shouldFilterOutFTOVariants =
              filterOptions?.findIndex(filter => {
                return (
                  filter.type === productOptionTypes[i] &&
                  filter.value.toLowerCase() !==
                  optionValue.trim().toLowerCase() &&
                  FINISHED_TO_ORDER !== optionValue
                )
              }) > -1

            const selectedLeg = getValueFromOptions(filterOptions, LEG)

            if (nameOfFilter === LEG.toLowerCase() && selectedLeg) {
              if (
                +selectedLeg < 32 &&
                variant.sku?.endsWith(LOWER_LEG_SLIM_LONG_SKU)
              ) {
                keepVariant = false
              }
              if (
                +selectedLeg >= 32 &&
                variant.sku?.endsWith(LOWER_LEG_SLIM_SHORT_SKU)
              ) {
                keepVariant = false
              }
            }

            if (
              nameOfFilter !== productOptionTypes[i] &&
              shouldFilterOutFTOVariants
            ) {
              keepVariant = false
            }

            const index = gatherVariant.findIndex(
              v => v.type === productOptionTypes[i]
            )

            if (index === -1) {
              newGatherVariant.push({
                type: productOptionTypes[i],
                values: [optionValue]
              })
            } else {
              newGatherVariant[index] = {
                ...newGatherVariant[index],
                values: [
                  ...new Set([...newGatherVariant[index].values, optionValue])
                ]
              }
            }
          })

        return keepVariant &&
          (variant.inventory_quantity > 0 ||
            variant.pre_order_inventory_quantity > 0)
          ? newGatherVariant
          : gatherVariant
      },
      []
    )
    : []

  return productOptionsList
}

export function filterOutSomeLowerLegVariants(
  variants: ProductVariant[] = [],
  selectedLegLength?: string
) {
  return variants.filter(variant => {
    if (selectedLegLength) {
      if (
        +selectedLegLength < 32 &&
        variant.sku.endsWith(LOWER_LEG_SLIM_LONG_SKU)
      ) {
        return false
      }

      if (
        +selectedLegLength >= 32 &&
        variant.sku.endsWith(LOWER_LEG_SLIM_SHORT_SKU)
      ) {
        return false
      }
    }

    return true
  })
}

function getValueFromOptions(filterOptions: FilterOption[], name: string) {
  return filterOptions.find(
    option => option.type.toLowerCase() === name.toLowerCase()
  )?.value
}

export function getVariantItem(
  product: ProductWithVariants | EmptyObject = {},
  filterOptions: FilterOption[] = []
) {
  const productOptions = getValueFromObject<ProductOption[]>(
    product,
    'options',
    []
  )
  const productVariants = getValueFromObject<ProductVariant[]>(
    product,
    'variants',
    []
  )
  const selectedLegLength = getValueFromOptions(filterOptions, LEG)

  const filteredVariants =
    productVariants.filter(
      variant =>
        (variant.inventory_quantity > 0 ||
          variant.pre_order_inventory_quantity > 0) &&
        isMatchingVariantForOptions({ productOptions, variant, filterOptions })
    ) || []

  return filterOutSomeLowerLegVariants(filteredVariants, selectedLegLength)
}

export function createFilterStorage(filterOptions: FilterOption[]) {
  return filterOptions.reduce((filters, o) => {
    return (
      ADDITIONAL_PROPERTIES[o.type as keyof typeof ADDITIONAL_PROPERTIES] ||
      {}
    ).skipInCookies
      ? filters
      : {
        ...filters,
        [o.type === 'shortsleg' ? 'shortsLeg' : o.type]: /waist|leg/.test(
          o.type
        )
          ? parseInt(o.value, 10)
          : o.value.toString()
      }
  }, {})
}

export { getFiltersOptionsState } from './getFiltersOptionsState'
